import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { floatingHostFor, promoteToTopLayer, type FloatingHost } from '@/lib/topLayer';
import { cn } from '@/lib/utils';
import { FieldNote } from './FieldNote';

export type DatePickerMode = 'day' | 'month';

export interface DatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'value' | 'defaultValue'
> {
  label: ReactNode;
  labelClassName?: string;
  /**
   * Keeps the label for screen readers but takes it off the screen — for a
   * filter bar that already says what is being filtered. Mirrors `Input`'s
   * prop of the same name.
   */
  hideLabel?: boolean;
  error?: string;
  hint?: ReactNode;
  /** `YYYY-MM-DD`, or `YYYY-MM` in month mode. Controlled. */
  value?: string;
  defaultValue?: string;
  /**
   * Both inclusive. Days outside the window are shown but cannot be chosen.
   *
   * Typed as the DOM types them rather than narrowed to `string`, because
   * `register()` hands these through as `string | number` and narrowing would
   * make the component refuse the very spread that makes it a drop-in.
   */
  min?: string | number;
  max?: string | number;
  /** Shown as muted text until a date is chosen. */
  placeholder?: string;
  /** `sm` matches a filter row; `md` matches `Input` and `Select` on a form. */
  size?: 'sm' | 'md';
  /** Offers a clear button once a date is set — for an optional filter. */
  isClearable?: boolean;
  /** `month` picks a whole month (`YYYY-MM`) instead of a day. */
  mode?: DatePickerMode;
  /**
   * Days to point out on the calendar, and why — a holiday's name, say. Return
   * null for an ordinary day. Marked days stay selectable: the mark informs the
   * choice rather than forbidding it, and `min`/`max` remain the way to forbid.
   */
  dayNote?: (day: string) => string | null;
  /** What a marked day is, for the line under the calendar: "Holiday". */
  dayNoteLegend?: string;
  /** Custom text to display in the trigger button instead of the default date format */
  displayValue?: string;
  /** Custom icon to display in the trigger button */
  icon?: ReactNode;
  /** Custom className for the outer container */
  containerClassName?: string;
}

/**
 * The product's date field: a calendar drawn in our own tokens, so picking a
 * date of birth and picking a register day look and behave the same on every
 * screen and in every browser. The native control does not — Chrome, Firefox
 * and Safari each draw their own, none of them in our type scale or our dark
 * palette, and none of them reachable on a phone the same way.
 *
 * A real `<input type="date">` is still rendered — visually hidden, never
 * focusable — and remains the field's source of truth. Choosing a day writes to
 * it and dispatches a genuine `input` event, which is what keeps every existing
 * caller working unchanged: react-hook-form's `register`, controlled `onChange`
 * handlers reading `event.target.value`, and native form submission. The
 * alternative — synthesising fake event objects — would have needed a cast at
 * every boundary and would drift from the DOM the moment a form is reset.
 *
 * Built to mirror `Select`: same trigger, same panel, same focus and blur
 * behaviour, so the two read as one family (CLAUDE.md §7).
 */

// --- date arithmetic, on `YYYY-MM-DD` strings -------------------------------
//
// Kept as strings rather than `Date` objects on purpose. An institute date is a
// calendar day, not an instant: parsing `2026-08-15` into a `Date` makes it
// midnight UTC, which is the 14th in any negative-offset zone and starts
// shifting days the moment somebody travels. String maths cannot do that.

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_PATTERN = /^\d{4}-\d{2}$/;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

interface YearMonth {
  year: number;
  month: number; // 1-12
}

const pad = (value: number) => String(value).padStart(2, '0');

const toDayString = (year: number, month: number, day: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

const toMonthString = ({ year, month }: YearMonth) => `${year}-${pad(month)}`;

function isValidValue(value: string, mode: DatePickerMode): boolean {
  const pattern = mode === 'month' ? MONTH_PATTERN : DAY_PATTERN;
  if (!pattern.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  if (month < 1 || month > 12) return false;
  // `2026-02-31` matches the pattern and is not a date.
  return mode === 'month' || (day >= 1 && day <= daysInMonth(year, month));
}

/** Day 0 of the next month is the last day of this one, whatever its length. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 0 (Sunday) … 6 (Saturday) for the 1st of the month. */
function firstWeekday(year: number, month: number): number {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

function addMonths({ year, month }: YearMonth, delta: number): YearMonth {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

/** Walks `delta` days from a `YYYY-MM-DD`, rolling over months and years. */
function addDays(value: string, delta: number): string {
  const [year, month, day] = value.split('-').map(Number);
  // Date.UTC rolls over on its own: day 32 of April becomes 2 May.
  const moved = new Date(Date.UTC(year, month - 1, day + delta));
  return toDayString(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate());
}

/** Today where the person is, not where the server is. */
function todayValue(mode: DatePickerMode): string {
  const now = new Date();
  const day = toDayString(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return mode === 'month' ? day.slice(0, 7) : day;
}

/**
 * Both ends inclusive. String comparison is the right one here: `YYYY-MM-DD`
 * sorts lexicographically exactly as it sorts chronologically.
 */
function isOutOfRange(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return true;
  if (max && value > max) return true;
  return false;
}

/**
 * Writes a value to the real input the way a person would, so React sees it.
 *
 * React keeps its own record of an input's last value; assigning `node.value`
 * updates that record silently and the event that follows is then discarded as
 * a no-op — the long-standing reason "setting .value doesn't fire onChange".
 * Going through the prototype's setter leaves React's record stale, which is
 * what makes it read the next `input` event as a real edit.
 *
 * `<select>` needs none of this, which is why `Select` assigns directly: React
 * takes a change event on a select at face value.
 */
const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

function writeValue(node: HTMLInputElement, next: string) {
  if (nativeValueSetter) nativeValueSetter.call(node, next);
  else node.value = next;

  // `input` alone: React maps a text-like input's `onChange` onto it, and
  // dispatching `change` as well would deliver the same edit twice.
  node.dispatchEvent(new Event('input', { bubbles: true }));
}

// --- component ---------------------------------------------------------------

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    label,
    labelClassName,
    hideLabel = false,
    error,
    hint,
    value,
    defaultValue,
    min,
    max,
    placeholder = 'Select a date',
    size = 'md',
    isClearable = false,
    mode = 'day',
    dayNote,
    dayNoteLegend,
    displayValue,
    icon,
    containerClassName,
    className,
    id,
    disabled,
    required,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const labelId = `${fieldId}-label`;
  const panelId = `${fieldId}-panel`;
  const gridId = `${fieldId}-grid`;
  const cellId = (cellValue: string) => `${fieldId}-cell-${cellValue}`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  /** Where the panel is drawn, which a modal on the screen changes. */
  const [host, setHost] = useState<FloatingHost>(() => ({
    container: document.body,
    needsTopLayer: false,
  }));
  const [panelStyle, setPanelStyle] = useState<{
    top: number;
    left: number;
  } | null>(null);
  /** Which panel the popover is showing — days, or the 12-month shortcut. */
  const [isPickingMonth, setIsPickingMonth] = useState(mode === 'month');

  const isControlled = value !== undefined;

  // Narrowed once, here, so the comparisons below stay plain string maths.
  const minDay = min === undefined ? undefined : String(min);
  const maxDay = max === undefined ? undefined : String(max);

  // The hidden input is the source of truth when uncontrolled: react-hook-form
  // writes straight to the DOM node on mount and on `reset()`, without firing an
  // event, so mirroring it after each render is what keeps the button honest.
  const [domValue, setDomValue] = useState(() => String(defaultValue ?? ''));
  useEffect(() => {
    const node = inputRef.current;
    if (!node || isControlled) return;
    if (node.value !== domValue) setDomValue(node.value);
  });

  const selectedValue = isControlled ? String(value ?? '') : domValue;
  const hasValidSelection = Boolean(selectedValue) && isValidValue(selectedValue, mode);

  const today = useMemo(() => todayValue(mode), [mode]);

  /** The month the grid is showing. Follows the selection, else today. */
  const [visible, setVisible] = useState<YearMonth>(() => {
    const anchor = hasValidSelection ? selectedValue : today;
    const [year, month] = anchor.split('-').map(Number);
    return { year, month };
  });

  /** Which cell the keyboard is on. Only meaningful while the panel is open. */
  const [focusedDay, setFocusedDay] = useState<string>(() =>
    hasValidSelection ? selectedValue : today,
  );

  /** Keeps our handle on the panel, and lifts it clear of an open dialog. */
  const attachPanel = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (host.needsTopLayer) promoteToTopLayer(node);
    },
    [host.needsTopLayer],
  );

  /** Forwards the node to the caller's ref while keeping our own handle on it. */
  const attachInput = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  function openPanel() {
    if (disabled) return;

    // Opens on the current choice, so arrowing starts from where the user is.
    const anchor = hasValidSelection ? selectedValue : today;
    const [year, month] = anchor.split('-').map(Number);
    setVisible({ year, month });
    setFocusedDay(anchor);
    setIsPickingMonth(mode === 'month');
    // Asked each time rather than once: the same field is drawn on a page and
    // inside a dialog, and only the second needs the top layer.
    setHost(floatingHostFor(triggerRef.current));
    setIsOpen(true);
  }

  function closePanel() {
    setIsOpen(false);
    triggerRef.current?.focus();
  }

  function commit(next: string) {
    const node = inputRef.current;
    closePanel();
    if (!node || next === selectedValue) return;

    writeValue(node, next);
    setDomValue(next);
  }

  function clear() {
    const node = inputRef.current;
    if (!node || !selectedValue) return;

    writeValue(node, '');
    setDomValue('');
  }

  /**
   * The hidden input never receives focus, so its `blur` would never fire — and
   * forms validating on blur would never validate a date. Firing `focusout` on
   * it when focus genuinely leaves the field restores that.
   */
  function handleFieldBlur(event: ReactFocusEvent<HTMLDivElement>) {
    // Our own synthetic blur bubbles back through here; ignore it or it loops.
    if (event.nativeEvent.target === inputRef.current) return;

    const nextFocused = event.relatedTarget;
    if (nextFocused instanceof Node && event.currentTarget.contains(nextFocused)) return;
    if (nextFocused instanceof Node && panelRef.current?.contains(nextFocused)) return;

    setIsOpen(false);
    inputRef.current?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
  }

  // --- the grid ---------------------------------------------------------------

  /**
   * Six weeks, always. A fixed height stops the panel resizing as somebody pages
   * through the year, which is what makes the next arrow jump under the cursor.
   */
  const weeks = useMemo(() => {
    const lead = firstWeekday(visible.year, visible.month);
    const start = addDays(toDayString(visible.year, visible.month, 1), -lead);

    return Array.from({ length: 6 }, (_, week) =>
      Array.from({ length: 7 }, (_, weekday) => addDays(start, week * 7 + weekday)),
    );
  }, [visible]);

  function moveFocus(next: string) {
    setFocusedDay(next);
    const [year, month] = next.split('-').map(Number);
    if (year !== visible.year || month !== visible.month) setVisible({ year, month });
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (disabled) return;

    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openPanel();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      return;
    }
    if (event.key === 'Tab') {
      // Let focus leave naturally, but don't leave the panel hanging open.
      setIsOpen(false);
      return;
    }

    if (isPickingMonth) {
      const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 }[event.key];
      if (step) {
        event.preventDefault();
        setVisible((current) => addMonths(current, step));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        chooseMonth(visible);
      }
      return;
    }

    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (step) {
      event.preventDefault();
      moveFocus(addDays(focusedDay, step));
      return;
    }

    switch (event.key) {
      case 'PageUp':
        event.preventDefault();
        moveFocus(shiftMonth(focusedDay, -1));
        return;
      case 'PageDown':
        event.preventDefault();
        moveFocus(shiftMonth(focusedDay, 1));
        return;
      case 'Home':
        event.preventDefault();
        moveFocus(addDays(focusedDay, -new Date(`${focusedDay}T00:00:00.000Z`).getUTCDay()));
        return;
      case 'End':
        event.preventDefault();
        moveFocus(addDays(focusedDay, 6 - new Date(`${focusedDay}T00:00:00.000Z`).getUTCDay()));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOutOfRange(focusedDay, minDay, maxDay)) commit(focusedDay);
        return;
    }
  }

  /** Same day next month, pinned back when that month is shorter. */
  function shiftMonth(day: string, delta: number): string {
    const [year, month, date] = day.split('-').map(Number);
    const next = addMonths({ year, month }, delta);
    return toDayString(next.year, next.month, Math.min(date, daysInMonth(next.year, next.month)));
  }

  function chooseMonth(next: YearMonth) {
    if (mode === 'month') {
      commit(toMonthString(next));
      return;
    }
    // In day mode the month grid is a shortcut, not the answer.
    setVisible(next);
    setIsPickingMonth(false);
  }

  // --- placement and focus -----------------------------------------------------

    const updatePosition = useCallback(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    if (!trigger) return;

    const box = trigger.getBoundingClientRect();
    const panel = panelRef.current;
    const panelHeight = panel?.offsetHeight || (dayNote ? 360 : (isPickingMonth ? 230 : 295));
    const panelWidth = panel?.offsetWidth || (dayNote ? 352 : 304);

    const spaceBelow = window.innerHeight - box.bottom - 8;
    const spaceAbove = box.top - 8;
    const spaceRight = window.innerWidth - box.left - 8;
    const spaceLeft = box.right - 8;

    const fitsBelow = (box.bottom + 4 + panelHeight) <= (window.innerHeight - 8);
    const fitsAbove = (box.top - 4 - panelHeight) >= 8;

    let shouldOpenUpward = false;
    if (fitsBelow) {
      shouldOpenUpward = false;
    } else if (fitsAbove) {
      shouldOpenUpward = true;
    } else {
      shouldOpenUpward = spaceAbove > spaceBelow;
    }

    const fitsRight = spaceRight >= panelWidth;
    const fitsLeft = spaceLeft >= panelWidth;
    const shouldOpenToLeft = !fitsRight && (fitsLeft || spaceLeft > spaceRight);

    // Compute clamped top so it never overflows top or bottom of viewport
    const idealTop = shouldOpenUpward ? box.top - 4 - panelHeight : box.bottom + 4;
    const top = Math.max(8, Math.min(idealTop, window.innerHeight - panelHeight - 8));

    // Compute clamped left so it never overflows left or right of viewport
    const idealLeft = shouldOpenToLeft ? box.right - panelWidth : box.left;
    const left = Math.max(8, Math.min(idealLeft, window.innerWidth - panelWidth - 8));

    setPanelStyle({ top, left });
  }, [isOpen, dayNote, isPickingMonth]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, { passive: true, capture: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    return () => {
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (isOpen) {
      gridRef.current?.focus();
      updatePosition();
    }
  }, [isOpen, isPickingMonth, visible.year, visible.month, updatePosition]);

  // --- render ------------------------------------------------------------------

  const describedBy = error ? errorId : hint ? hintId : undefined;

  const monthIsOutOfRange = (candidate: YearMonth): boolean => {
    const first = `${toMonthString(candidate)}-01`;
    const last = toDayString(
      candidate.year,
      candidate.month,
      daysInMonth(candidate.year, candidate.month),
    );
    // A month is reachable if any day in it is.
    return Boolean(minDay && last < minDay) || Boolean(maxDay && first > maxDay);
  };

  const triggerLabel = displayValue ?? (hasValidSelection
    ? mode === 'month'
      ? `${MONTHS[Number(selectedValue.slice(5, 7)) - 1]} ${selectedValue.slice(0, 4)}`
      : formatChosenDay(selectedValue)
    : placeholder);

  return (
    <div className={cn("flex flex-col gap-1.5", containerClassName)}>
      <label htmlFor={fieldId} id={labelId} className={cn('lbl', labelClassName, hideLabel && 'sr-only')}>
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      <div className="relative" onKeyDown={handleKeyDown} onBlur={handleFieldBlur}>
        <input
          ref={attachInput}
          type={mode === 'month' ? 'month' : 'date'}
          value={value}
          defaultValue={defaultValue}
          min={min}
          max={max}
          disabled={disabled}
          // Present for the form, absent for the user: the button below is the
          // control people see, focus and operate.
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          {...props}
        />
        {/*
          `required` is deliberately not forwarded to the input above.
          The browser refuses to submit a form holding an invalid required
          control and then tries to focus it to say so — and this one cannot be
          focused, so the submit would fail silently with nothing on screen to
          explain it. The trigger carries `aria-required` instead, and the
          form's own validation reports the problem, which is how every form in
          this product already works.
        */}

        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? panelId : undefined}
          aria-labelledby={`${labelId} ${fieldId}`}
          aria-required={required || undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onClick={() => (isOpen ? closePanel() : openPanel())}
          className={cn(
            'flex w-full items-center gap-2 rounded-field border bg-surface text-left text-ink',
            'transition-colors duration-150',
            'focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3',
            size === 'sm' ? 'h-9 px-2.5 text-sm' : 'h-10 px-3 text-sm',
            isClearable && hasValidSelection && (size === 'sm' ? 'pr-7' : 'pr-8'),
            error ? 'border-crit' : 'border-line',
            className,
          )}
        >
          {icon ?? (
            <CalendarDays
              className={cn('shrink-0 text-ink-3', size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4')}
              aria-hidden
            />
          )}
          <span className={cn('min-w-0 flex-1 truncate', !hasValidSelection && !displayValue && 'text-ink-3')}>
            {triggerLabel}
          </span>
        </button>

        {isClearable && hasValidSelection && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={typeof label === 'string' ? `Clear ${label.toLowerCase()}` : 'Clear date'}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 rounded-field p-1 text-ink-3',
              'hover:bg-surface-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent/25',
              size === 'sm' ? 'right-1.5' : 'right-2',
            )}
          >
            <X className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />
          </button>
        )}

        {isOpen && panelStyle && createPortal(
          <div
            ref={attachPanel}
            id={panelId}
            role="dialog"
            aria-label={typeof label === 'string' ? label : 'Date picker'}
            style={{
              position: 'fixed',
              top: panelStyle.top,
              left: panelStyle.left,
              maxHeight: 'calc(100vh - 16px)',
              zIndex: 9999,
            }}
            className={cn(
              // Wider when days are marked, so the name under each one has room.
              dayNote ? 'w-[22rem]' : 'w-[19rem]',
              'max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden custom-scrollbar rounded-card border border-line bg-surface shadow-card',
              'animate-pop-in',
            )}
            // Keeps focus in the grid while clicking a day, so the field never
            // blurs itself before the click lands.
            onMouseDown={(event) => event.preventDefault()}
          >
            <div className="flex items-center justify-between border-b border-line px-2 py-2">
              <button
                type="button"
                onClick={() =>
                  setVisible((current) => addMonths(current, isPickingMonth ? -12 : -1))
                }
                aria-label={isPickingMonth ? 'Previous year' : 'Previous month'}
                className="rounded-field p-1.5 text-ink-2 hover:bg-surface-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent/25"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>

              <button
                type="button"
                onClick={() => setIsPickingMonth((current) => (mode === 'month' ? true : !current))}
                aria-label={isPickingMonth ? `Showing ${visible.year}` : 'Choose a different month'}
                className="rounded-field px-2 py-1 text-sm font-[560] text-ink hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/25"
              >
                {isPickingMonth ? visible.year : `${MONTHS[visible.month - 1]} ${visible.year}`}
              </button>

              <button
                type="button"
                onClick={() => setVisible((current) => addMonths(current, isPickingMonth ? 12 : 1))}
                aria-label={isPickingMonth ? 'Next year' : 'Next month'}
                className="rounded-field p-1.5 text-ink-2 hover:bg-surface-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent/25"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {isPickingMonth ? (
              <div
                ref={gridRef}
                tabIndex={-1}
                role="grid"
                aria-label={`Months in ${visible.year}`}
                className="grid grid-cols-3 gap-1 p-2 focus:outline-none"
              >
                {MONTHS.map((monthName, index) => {
                  const candidate = { year: visible.year, month: index + 1 };
                  const isDisabled = monthIsOutOfRange(candidate);
                  const isSelected =
                    hasValidSelection && selectedValue.slice(0, 7) === toMonthString(candidate);

                  return (
                    <button
                      key={monthName}
                      type="button"
                      role="gridcell"
                      aria-selected={isSelected}
                      disabled={isDisabled}
                      onClick={() => chooseMonth(candidate)}
                      className={cn(
                        'rounded-field py-2 text-sm text-ink transition-colors',
                        'hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/25',
                        isSelected && 'bg-pick text-pick-on hover:bg-pick',
                        isDisabled &&
                          'cursor-not-allowed text-ink-3 line-through hover:bg-transparent',
                      )}
                    >
                      {monthName.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-0.5 px-2 pt-2" aria-hidden>
                  {WEEKDAYS.map((weekday) => (
                    <span
                      key={weekday}
                      className="py-1 text-center text-2xs font-[560] uppercase tracking-wide text-ink-3"
                    >
                      {weekday.slice(0, 1)}
                    </span>
                  ))}
                </div>

                <div
                  ref={gridRef}
                  id={gridId}
                  tabIndex={-1}
                  role="grid"
                  aria-label={`${MONTHS[visible.month - 1]} ${visible.year}`}
                  aria-activedescendant={cellId(focusedDay)}
                  className="grid grid-cols-7 gap-0.5 px-2 pb-2 focus:outline-none"
                >
                  {weeks.flat().map((day) => {
                    const isThisMonth = Number(day.slice(5, 7)) === visible.month;
                    const isSelected = day === selectedValue;
                    const isToday = day === today;
                    const isDisabled = isOutOfRange(day, minDay, maxDay);
                    const isFocused = day === focusedDay;
                    const note = dayNote?.(day) ?? null;

                    return (
                      <button
                        key={day}
                        id={cellId(day)}
                        type="button"
                        role="gridcell"
                        tabIndex={-1}
                        aria-selected={isSelected}
                        aria-current={isToday ? 'date' : undefined}
                        aria-label={note ? `${formatChosenDay(day)}, ${note}` : undefined}
                        title={note ?? undefined}
                        disabled={isDisabled}
                        onClick={() => commit(day)}
                        onMouseMove={() => setFocusedDay(day)}
                        className={cn(
                          'rounded-field text-sm text-ink transition-colors',
                          // With marks on, every day gets a second line, as a
                          // phone's calendar does, so the rows stay one height.
                          dayNote ? 'flex h-9.5 flex-col items-center justify-start gap-0.5 pt-0.5' : 'h-8',
                          'hover:bg-surface-2 focus:outline-none',
                          !isThisMonth && 'text-ink-3',
                          note && !isSelected && 'font-[560] text-crit',
                          isToday && !isSelected && 'font-[560] text-pick',
                          isFocused && !isSelected && 'ring-2 ring-accent/25',
                          isSelected && 'bg-pick font-[560] text-pick-on hover:bg-pick',
                          isDisabled &&
                            'cursor-not-allowed text-ink-3 opacity-40 hover:bg-transparent',
                        )}
                      >
                        {dayNote ? (
                          <>
                            {/* A marked day is ringed, and its name sits
                                underneath — the way a phone calendar shows a
                                holiday. The full name is in the title and
                                the line below the grid. */}
                            <span
                              className={cn(
                                'grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-semibold',
                                note && !isSelected && 'bg-crit text-paper',
                              )}
                            >
                              {Number(day.slice(8, 10))}
                            </span>
                            <span
                              className={cn(
                                'w-full truncate px-0.5 text-center text-[10px] leading-tight',
                                isSelected ? 'text-pick-on' : 'text-crit',
                              )}
                              aria-hidden
                            >
                              {note}
                            </span>
                          </>
                        ) : (
                          Number(day.slice(8, 10))
                        )}
                      </button>
                    );
                  })}
                </div>

                {dayNote && (
                  <DayNoteLine
                    focusedNote={dayNote(focusedDay)}
                    legend={dayNoteLegend}
                    isShown={weeks.flat().some((day) => dayNote(day) !== null)}
                  />
                )}
              </>
            )}

            <div className="flex items-center justify-between border-t border-line px-2 py-1.5">
              <button
                type="button"
                onClick={() => {
                  if (isOutOfRange(today, minDay, maxDay)) return;
                  commit(today);
                }}
                disabled={isOutOfRange(today, minDay, maxDay)}
                className="rounded-field px-2 py-1 text-sm font-[560] text-pick hover:bg-pick-soft focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:cursor-not-allowed disabled:text-ink-3 disabled:hover:bg-transparent"
              >
                {mode === 'month' ? 'This month' : 'Today'}
              </button>

              {isClearable && hasValidSelection && (
                <button
                  type="button"
                  onClick={() => {
                    clear();
                    closePanel();
                  }}
                  className="rounded-field px-2 py-1 text-sm text-ink-2 hover:bg-surface-2 hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent/25"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        , host.container)}
      </div>

      <FieldNote error={error} hint={hint} errorId={errorId} hintId={hintId} />
    </div>
  );
});

/**
 * The line under the calendar that says what the marked days are: the name of
 * the one under the pointer or keyboard focus, or the legend otherwise. Only
 * drawn for a month that has a marked day, so an ordinary month stays quiet.
 * `aria-live` so a keyboard user hears the holiday they have moved onto.
 */
function DayNoteLine({
  focusedNote,
  legend,
  isShown,
}: {
  focusedNote: string | null;
  legend: string | undefined;
  isShown: boolean;
}) {
  if (!isShown) return null;

  return (
    <p aria-live="polite" className="flex items-center gap-2 px-3 pb-2 text-xs text-ink-2">
      <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-crit" aria-hidden />
      <span className="truncate">{focusedNote ?? legend ?? 'Marked days'}</span>
    </p>
  );
}

/** `2026-08-15` → `15 Aug 2026`, without going through a `Date`. */
function formatChosenDay(value: string): string {
  const [year, month, day] = value.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1].slice(0, 3)} ${year}`;
}
